/**
 * ADR-220 «قد يعجبك» on the web — the app's rail, same server, same rules.
 *
 * 1. Signals (device only, purged at sign-out): product views with dwell,
 *    adds to cart, the first result ids of a search and its clicks
 *    (ErosRecoRules; never a search's text).
 * 2. Rails: every [data-eros-reco-rail] asks POST /v1/reco when scrolled into
 *    view, renders the ranked ids through sections/eros-reco-cards, and
 *    reports GA4 view_item_list / select_item with the algorithm version.
 *    data-fallback="keep" leaves the server-rendered list when the API has
 *    nothing (product page SEO list, empty cart best sellers);
 *    "hide" removes the rail. Shopify's own recommendations are never used.
 */
(function () {
  'use strict';

  var R = window.ErosRecoRules;
  if (!R) return;

  var VISIBLE = 10;
  var DID_KEY = 'eros_reco_did_v1';
  var CART_KEY = 'eros_reco_cart_pids_v1';

  function now() {
    return Math.floor(Date.now() / 1000);
  }

  function load() {
    try {
      return R.parse(window.localStorage.getItem(R.STORE_KEY));
    } catch (e) {
      return R.empty();
    }
  }

  function update(change) {
    try {
      window.localStorage.setItem(R.STORE_KEY, JSON.stringify(change(load())));
    } catch (e) {
      /* storage blocked: signals simply stay empty */
    }
  }

  function deviceId() {
    try {
      var id = window.localStorage.getItem(DID_KEY);
      if (!id) {
        id = 'web_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
        window.localStorage.setItem(DID_KEY, id);
      }
      return id;
    } catch (e) {
      return 'web_anon';
    }
  }

  function meta() {
    return (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
  }

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---------- signals ---------- */

  function trackProductView() {
    var m = meta();
    if (!m.page || m.page.pageType !== 'product' || !m.product) return null;
    var gid = R.productGid(m.product.id);
    if (!gid) return null;
    update(function (s) { return R.recordView(s, gid, now()); });
    var opened = Date.now();
    function close() {
      var dwell = Math.round((Date.now() - opened) / 1000);
      update(function (s) { return R.closeView(s, gid, dwell); });
    }
    window.addEventListener('pagehide', close);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') close();
    });
    return gid;
  }

  function cartProductIds(cart) {
    return ((cart && cart.items) || []).map(function (i) { return R.productGid(i.product_id); }).filter(Boolean);
  }

  function trackAdds() {
    document.addEventListener('variant:add', function (e) {
      var items = (e.detail && e.detail.items) || [];
      items.forEach(function (i) {
        var gid = R.productGid(i.product_id);
        if (gid) update(function (s) { return R.recordAdd(s, gid, now()); });
      });
    });
    document.addEventListener('cart:change', function (e) {
      var cart = e.detail && e.detail.cart;
      if (!cart || !cart.items) return;
      var current = cartProductIds(cart);
      var before = null;
      try {
        before = JSON.parse(window.sessionStorage.getItem(CART_KEY) || 'null');
      } catch (err) {
        before = null;
      }
      if (Array.isArray(before)) {
        current.forEach(function (gid) {
          if (before.indexOf(gid) < 0) update(function (s) { return R.recordAdd(s, gid, now()); });
        });
      }
      try {
        window.sessionStorage.setItem(CART_KEY, JSON.stringify(current));
      } catch (err) {
        /* ignore */
      }
      scheduleCartRails();
    });
  }

  function trackSearch() {
    var m = meta();
    if (!m.page || m.page.pageType !== 'searchresults') return;
    var ids = [];
    document.querySelectorAll('#main product-card [data-product-id], #main [data-product-id]').forEach(function (el) {
      var gid = R.productGid(el.getAttribute('data-product-id'));
      if (gid && ids.indexOf(gid) < 0) ids.push(gid);
    });
    if (!ids.length) return;
    update(function (s) { return R.recordSearch(s, ids.slice(0, 3), now()); });
    document.addEventListener('click', function (e) {
      var card = e.target.closest && e.target.closest('#main product-card');
      var idEl = card && card.querySelector('[data-product-id]');
      var gid = idEl && R.productGid(idEl.getAttribute('data-product-id'));
      if (gid) update(function (s) { return R.recordSearchClick(s, gid); });
    });
  }

  /* ---------- rails ---------- */

  function gtag() {
    if (typeof window.gtag === 'function') window.gtag.apply(null, arguments);
  }

  function versionParams(surface, v) {
    var parts = String(v || '').split('.');
    return { reco_surface: surface, algo_version: parts[0] || '', snap_id: parts[1] || '' };
  }

  function fetchCart() {
    return fetch(routesRoot() + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function renderCards(ids, limit) {
    var url = routesRoot() + 'search?type=product&q=' + encodeURIComponent(R.searchQuery(ids)) + '&section_id=eros-reco-cards';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var cards = Array.prototype.slice.call(doc.querySelectorAll('[data-eros-reco-card]'));
        var available = cards.filter(function (c) { return c.getAttribute('data-available') === 'true'; });
        /* The theme's product-card is the carousel/grid cell; the wrapper's
           data attributes move onto it for click tracking. */
        return available.slice(0, limit).map(function (wrap) {
          var cell = wrap.firstElementChild || wrap;
          ['data-eros-reco-card', 'data-product-id', 'data-product-handle'].forEach(function (a) {
            cell.setAttribute(a, wrap.getAttribute(a) || '');
          });
          return cell;
        });
      });
  }

  /**
   * Put the cards in the rail. A theme scroll-carousel is rebuilt rather than
   * refilled (its cells are measured once), together with its prev/next
   * buttons so they bind to the new carousel. An empty container with
   * data-eros-reco-carousel gets a carousel only now: the theme's carousel
   * throws when it measures with no cells.
   */
  function fill(list, cards) {
    var carouselClass = list.getAttribute('data-eros-reco-carousel');
    if (carouselClass) {
      var made = document.createElement('scroll-carousel');
      made.className = carouselClass;
      made.setAttribute('allow-drag', '');
      cards.forEach(function (c) { made.appendChild(c); });
      list.replaceChildren(made);
      return;
    }
    var old = list.tagName === 'SCROLL-CAROUSEL' ? list : list.querySelector('scroll-carousel');
    if (!old) {
      list.replaceChildren.apply(list, cards);
      return;
    }
    var fresh = old.cloneNode(false);
    cards.forEach(function (c) { fresh.appendChild(c); });
    if (old === list) {
      list.replaceWith(fresh);
      return;
    }
    var shell = old.parentElement;
    var rebuilt = shell.cloneNode(false);
    Array.prototype.forEach.call(shell.children, function (child) {
      rebuilt.appendChild(child === old ? fresh : child.cloneNode(true));
    });
    shell.replaceWith(rebuilt);
  }

  function fail(rail) {
    if (rail.getAttribute('data-fallback') !== 'keep') rail.hidden = true;
  }

  function loadRail(rail) {
    if (rail.__erosRecoBusy) return;
    rail.__erosRecoBusy = true;
    var surface = rail.getAttribute('data-surface');
    var product = R.productGid(rail.getAttribute('data-product-id'));
    var needsCart = surface === 'cart' || surface === 'pdp_pair';
    (needsCart ? fetchCart() : Promise.resolve(null))
      .then(function (cart) {
        var body = { surface: surface, sig: R.toWire(load(), now()), limit: 12, did: deviceId() };
        if (product) body.product = product;
        var cartIds = cartProductIds(cart).slice(0, 12);
        if (cartIds.length) body.cart = cartIds;
        if (surface === 'cart' && !cartIds.length) throw new Error('empty_cart');
        return window.ErosCommerce.request('/v1/reco', { method: 'POST', body: body });
      })
      .then(function (res) {
        var ids = ((res && res.items) || []).map(function (i) { return i.id; });
        if (!ids.length) throw new Error('no_items');
        var limit = Math.min(VISIBLE, parseInt(rail.getAttribute('data-limit'), 10) || VISIBLE);
        return renderCards(ids, limit).then(function (cards) {
          if (!cards.length) throw new Error('no_cards');
          fill(rail.querySelector('[data-eros-reco-list]'), cards);
          rail.hidden = false;
          var params = versionParams(surface, res.v);
          var listId = 'reco_' + surface;
          gtag('event', 'view_item_list', Object.assign({ item_list_id: listId, item_count: cards.length }, params));
          cards.forEach(function (card, index) {
            card.addEventListener('click', function () {
              var gid = R.productGid(card.getAttribute('data-product-id'));
              if (gid && res.v) {
                try {
                  var hits = JSON.parse(window.localStorage.getItem(R.HITS_KEY) || '[]');
                  var hit = { p: gid, s: surface, v: res.v, t: now() };
                  window.localStorage.setItem(R.HITS_KEY, JSON.stringify(R.recordHit(hits, hit, now())));
                } catch (e) {
                  /* ignore */
                }
              }
              var src = '';
              (res.items || []).forEach(function (i) {
                if (R.productGid(i.id) === R.productGid(card.getAttribute('data-product-id'))) src = i.s || '';
              });
              gtag('event', 'select_item', Object.assign({
                item_list_id: listId,
                items: [{ item_id: card.getAttribute('data-product-handle'), index: index }],
                reco_src: src
              }, params));
            });
          });
        });
      })
      .catch(function () {
        fail(rail);
      })
      .then(function () {
        rail.__erosRecoBusy = false;
      });
  }

  function observe(rail) {
    if (rail.__erosRecoObserved) return;
    rail.__erosRecoObserved = true;
    if (!('IntersectionObserver' in window)) return loadRail(rail);
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.disconnect();
        loadRail(rail);
      });
    }, { rootMargin: '200px' });
    io.observe(rail);
  }

  function scan(root) {
    (root || document).querySelectorAll('[data-eros-reco-rail]').forEach(observe);
  }

  var cartTimer = null;
  function scheduleCartRails() {
    clearTimeout(cartTimer);
    cartTimer = setTimeout(function () {
      document.querySelectorAll('[data-eros-reco-rail][data-surface="cart"]').forEach(function (rail) {
        if (rail.__erosRecoObserved) loadRail(rail);
      });
    }, 800);
  }

  function purgeOnSignOut() {
    if (!window.ErosSession || !window.ErosSession.onChange) return;
    var wasSignedIn = null;
    window.ErosSession.onChange(function (session) {
      var purge = R.isSignOut(wasSignedIn, session);
      wasSignedIn = !!(session && session.customerGid);
      if (!purge) return;
      try {
        window.localStorage.removeItem(R.STORE_KEY);
        window.localStorage.removeItem(R.HITS_KEY);
      } catch (e) {
        /* ignore */
      }
    });
  }

  function boot() {
    trackProductView();
    trackAdds();
    trackSearch();
    purgeOnSignOut();
    scan(document);
    document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
    /* the cart drawer re-renders its section: pick up new rails once per frame */
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        scan(document);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
